import React from 'react';

import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import CtrlFView from 'ui-library/CtrlFView';

import {Editor} from '../EditorTypes';
import {MetadataForRestore} from '../selection/block/MultiSelectListUtil';
import {MEDIOCRE_OVERLAY_PROPS} from './DOM';
import {DEBOUNCE_INTERVAL_MS, PLACEHOLDER_TEXT} from './SearchConstants';
import useCtrlF, {MediocreSearchState} from './useCtrlF';

export interface CtrlFInputOverlayProps<TEditor extends Editor<TRoot, TItem>, TRoot, TItem> {
  docRoot: TRoot;
  editor: TEditor;
  searchableItems: Array<TItem>;
  focusedItem?: TItem;
  searchState: MediocreSearchState | undefined;
  setSearchState: (searchState: MediocreSearchState) => void;
  requestSelection: (selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex>) => void;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  query: string;
  setQuery: (query: string) => void;
}

export default function CtrlFInputOverlay<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>({
  docRoot,
  editor,
  searchableItems,
  focusedItem,
  searchState,
  setSearchState,
  requestSelection,
  isOpen,
  setOpen,
  query,
  setQuery,
}: CtrlFInputOverlayProps<TEditor, TRoot, TItem>): JSX.Element | null {
  const {ref, doClose, isLoading, isTruncated, searchResults, selectedIndex, setSelectedIdx} = useCtrlF<
    TEditor,
    TRoot,
    TItem
  >(
    editor,
    docRoot,
    searchableItems,
    focusedItem ?? null,
    searchState,
    setSearchState,
    DEBOUNCE_INTERVAL_MS,
    isOpen,
    setOpen,
    query,
    setQuery
  );

  if (!isOpen) {
    return null;
  }
  return (
    <div {...MEDIOCRE_OVERLAY_PROPS}>
      <CtrlFView
        ref={ref}
        onClose={() => {
          doClose();
          const result = searchResults[selectedIndex];
          if (result?.selection) {
            requestSelection(result.selection);
          }
        }}
        onQueryChange={(newQuery) => {
          setQuery(newQuery);
        }}
        query={query}
        onViewNextResult={() => {
          setSelectedIdx(selectedIndex + 1);
        }}
        onViewPreviousResult={() => {
          setSelectedIdx(selectedIndex - 1);
        }}
        selectedIndex={selectedIndex}
        numSearchResults={searchResults.length}
        isTruncated={isTruncated}
        isLoading={isLoading}
        placeholder={PLACEHOLDER_TEXT}
      />
    </div>
  );
}
